/**
 * TREATX-MSG-UX-SYSTEM-EVENTS-01 — presentation-only grouping of
 * consecutive system events.
 *
 * System events are 76% of all stored messages in a busy thread and were
 * rendered as individual full-width pills, burying the actual conversation.
 * This component collapses a RUN of consecutive system events into one
 * compact, expandable row. Nothing is filtered, re-ordered, summarised or
 * written: expanding renders exactly the same bubbles, in the same order,
 * with the same timestamps and metadata.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';

/** Minimum run length that is worth collapsing. */
export const SYSTEM_GROUP_MIN = 2;

/**
 * Split a chronological message list into render items, collapsing runs of
 * consecutive system messages. Patient and staff messages are never grouped.
 */
export const groupConsecutiveSystemEvents = (messages, isSystem) => {
  const test = isSystem || ((m) => m?.message_type === 'system');
  const items = [];
  let run = [];
  const flush = () => {
    if (!run.length) return;
    if (run.length >= SYSTEM_GROUP_MIN) {
      items.push({ kind: 'system-group', key: `sysgrp-${run[0].id}`, messages: run });
    } else {
      run.forEach((m) => items.push({ kind: 'message', key: m.id, message: m }));
    }
    run = [];
  };
  for (const m of messages || []) {
    if (test(m)) {
      run.push(m);
    } else {
      flush();
      items.push({ kind: 'message', key: m.id, message: m });
    }
  }
  flush();
  return items;
};

export const SystemEventGroup = ({ count, groupId, children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = `system-group-panel-${groupId}`;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="my-3" data-testid={`system-group-${groupId}`}>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={t('provider.messaging.systemGroup.ariaLabel', { count })}
          className="group flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
          data-testid={`system-group-toggle-${groupId}`}
        >
          <Info className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="font-medium">
            {t('provider.messaging.systemGroup.summary', { count })}
          </span>
          <span className="text-slate-400 group-hover:text-slate-600">
            {' · '}
            {open
              ? t('provider.messaging.systemGroup.hideDetails')
              : t('provider.messaging.systemGroup.showDetails')}
          </span>
          <Chevron className="h-3 w-3 shrink-0" aria-hidden="true" />
        </button>
      </div>
      <div id={panelId} hidden={!open} data-testid={`system-group-panel-${groupId}`}>
        {open ? children : null}
      </div>
    </div>
  );
};

export default SystemEventGroup;
